<script setup lang="ts">
import { computed } from 'vue'
import { useGetMyOpenArticleAnalysesQuery, AnalysisStatus, Role } from '@/graphql/types'
import { watchErrors } from '@/notifications'
import auth from '@/auth/keycloak'

// The analyses queries require MANAGE_ARTICLES or MANAGE_OWN_ARTICLES server-side
// (own-article scoped). Gate the poll so users with neither role never issue it
// (avoids a repeating Access-Denied error).
const canAccessAnalyses = auth.hasRole(Role.MANAGE_ARTICLES) || auth.hasRole(Role.MANAGE_OWN_ARTICLES)

const { result, error } = useGetMyOpenArticleAnalysesQuery({
  pollInterval: 10000,
  enabled: canAccessAnalyses,
})

const analyses = computed(() => result.value?.myOpenArticleAnalyses ?? [])

watchErrors({ error, description: 'Eigene Analysen konnten nicht geladen werden.' })

function bannerClass(status: string): string {
  switch (status) {
    case AnalysisStatus.PENDING:
    case AnalysisStatus.RUNNING:
      return 'bg-blue-100 border-blue-300 text-blue-900'
    case AnalysisStatus.COMPLETED:
      return 'bg-green-100 border-green-300 text-green-900'
    case AnalysisStatus.FAILED:
      return 'bg-red-100 border-red-300 text-red-900'
    default:
      return 'bg-gray-100 border-gray-300 text-gray-900'
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case AnalysisStatus.PENDING: return 'Wartend'
    case AnalysisStatus.RUNNING: return 'Analyse läuft…'
    case AnalysisStatus.COMPLETED: return 'Analyse abgeschlossen'
    case AnalysisStatus.FAILED: return 'Analyse fehlgeschlagen'
    default: return status
  }
}

function isPulse(status: string): boolean {
  return status === AnalysisStatus.PENDING || status === AnalysisStatus.RUNNING
}
</script>

<template>
  <div v-if="analyses.length" class="space-y-1">
    <div
      v-for="analysis in analyses"
      :key="analysis.id"
      :class="['flex items-center gap-3 px-4 py-2 border-b text-sm', bannerClass(analysis.status)]"
    >
      <span v-if="isPulse(analysis.status)" class="relative flex h-2 w-2">
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-blue-500"></span>
        <span class="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
      </span>

      <span class="font-medium">{{ analysis.articleSubject }}</span>
      <span class="opacity-75">– {{ statusLabel(analysis.status) }}</span>

      <div class="ml-auto flex items-center gap-2">
        <router-link
          v-if="analysis.status === AnalysisStatus.COMPLETED"
          :to="{ name: 'article-analysis', params: { id: analysis.articleId, analysisId: analysis.id } }"
          class="btn btn-xs btn-success"
        >
          Ergebnis ansehen
        </router-link>
        <router-link
          v-else-if="analysis.status === AnalysisStatus.FAILED"
          :to="{ name: 'article-analysis', params: { id: analysis.articleId, analysisId: analysis.id } }"
          class="btn btn-xs btn-error"
        >
          Details
        </router-link>
      </div>
    </div>
  </div>
</template>
